import { MutationTree } from 'vuex';
import { ErrorDetail } from '../../types';
import {
    NodeState,
    Node,
    NodePasswordStrengthSetting,
    NodeSamlFederationInfo,
    NodePlaceholder,
    SamlNode,
} from './types';
import { NodeGroup } from '../node_group/types';
import * as types from './mutation-types';

// 更新・削除のエラーをクリアする
const resetChangesAllErrors = (state: NodeState): void => {
    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;

    const osConnectionState = state.osConnection;
    osConnectionState.error = false;
    osConnectionState.errorTitle = undefined;
    osConnectionState.errorDetails = undefined;

    const osCreatedConnectionState = state.osCreatedConnection;
    osCreatedConnectionState.error = false;
    osCreatedConnectionState.errorTitle = undefined;
    osCreatedConnectionState.errorDetails = undefined;

    const domainConnectionState = state.domainConnection;
    domainConnectionState.error = false;
    domainConnectionState.errorTitle = undefined;
    domainConnectionState.errorDetails = undefined;

    const csvState = state.csv;
    csvState.error = false;
    csvState.errorCode = undefined;
    csvState.errorMsg = undefined;

    const addAppsState = state.addApps;
    addAppsState.error = false;
    addAppsState.errorTitle = undefined;
    addAppsState.errorDetails = undefined;

    const removeAppsState = state.removeApps;
    removeAppsState.error = false;
    removeAppsState.errorTitle = undefined;
    removeAppsState.errorDetails = undefined;

    const addExcludesState = state.addExcludes;
    addExcludesState.error = false;
    addExcludesState.errorTitle = undefined;
    addExcludesState.errorDetails = undefined;

    const removeExcludesState = state.removeExcludes;
    removeExcludesState.error = false;
    removeExcludesState.errorTitle = undefined;
    removeExcludesState.errorDetails = undefined;

    const samlCertificateDownloadState = state.samlCertificateDownload;
    samlCertificateDownloadState.error = false;
    samlCertificateDownloadState.errorTitle = undefined;
    samlCertificateDownloadState.errorDetails = undefined;

    const samlCertificateReissueState = state.samlCertificateReissue;
    samlCertificateReissueState.error = false;
    samlCertificateReissueState.errorTitle = undefined;
    samlCertificateReissueState.errorDetails = undefined;

    const samlMetadataDownloadState = state.samlMetadataDownload;
    samlMetadataDownloadState.error = false;
    samlMetadataDownloadState.errorTitle = undefined;
    samlMetadataDownloadState.errorDetails = undefined;

    const loginHistoryCollectionIntervalTypeAcquisition = state.loginHistoryCollectionIntervalTypeAcquisition;
    loginHistoryCollectionIntervalTypeAcquisition.error = false;
    loginHistoryCollectionIntervalTypeAcquisition.errorTitle = undefined;
    loginHistoryCollectionIntervalTypeAcquisition.errorDetails = undefined;
};

export const mutations: MutationTree<NodeState> = {
    // ノード検索系
    [types.SEARCH_START](state) {
        const searchState = state.search;
        searchState.error = false;
        searchState.errorCode = undefined;
        searchState.errorMsg = undefined;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; nodes: Array<Node> }) {
        state.nodeCount = payload.count;
        state.nodes = payload.nodes;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const searchState = state.search;
        searchState.errorCode = payload.code;
        searchState.errorMsg = payload.message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // ノード作成系
    [types.NEW_NODE](state) {
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isCreated = false;

        const osConnectionState = state.osConnection;
        osConnectionState.error = false;
        osConnectionState.errorTitle = undefined;
        osConnectionState.errorDetails = undefined;

        const domainConnectionState = state.domainConnection;
        domainConnectionState.error = false;
        domainConnectionState.errorTitle = undefined;
        domainConnectionState.errorDetails = undefined;
    },

    [types.CREATE_START](state) {
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state) {
        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // ノード詳細情報参照系
    [types.SHOW_START](state) {
        resetChangesAllErrors(state);
        const showState = state.show;
        showState.error = false;
        showState.errorCode = undefined;
        showState.errorMsg = undefined;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](
        state,
        payload: {
            node: Node;
            group: Array<NodeGroup>;
            node_password_strength_setting: NodePasswordStrengthSetting;
            node_saml_federation_info: NodeSamlFederationInfo;
            node_placeholders: Array<NodePlaceholder>;
        }
    ) {
        const showState = state.show;
        state.nodeDetails.node = payload.node;
        state.nodeDetails.nodePasswordStrengthSetting = payload.node_password_strength_setting;
        state.nodeDetails.nodeSamlFederationInfo = payload.node_saml_federation_info;
        state.nodeDetails.nodeGroups = payload.group.length > 0 ? payload.group : [];
        state.nodeDetails.nodePlaceholders = payload.node_placeholders;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // ノード更新系
    [types.UPDATE_START](state) {
        resetChangesAllErrors(state);
        const updateState = state.update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_SUCCESS](state) {
        const updateState = state.update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // ノード削除系
    [types.DESTROY_START](state) {
        resetChangesAllErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // ノード管理OSアカウント接続テスト系(ノード作成前)
    [types.OS_CONNECTION_START](state) {
        resetChangesAllErrors(state);
        const osConnectionState = state.osConnection;
        osConnectionState.isProcessing = true;
    },

    [types.OS_CONNECTION_SUCCESS](state) {
        const osConnectionState = state.osConnection;
        osConnectionState.isProcessing = false;
    },

    [types.OS_CONNECTION_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const osConnectionState = state.osConnection;
        osConnectionState.errorTitle = payload.errorTitle;
        osConnectionState.errorDetails = payload.errorDetails;
        osConnectionState.error = true;
        osConnectionState.isProcessing = false;
    },

    // ノード管理OSアカウント接続テスト系(ノード作成後)
    [types.OS_CREATED_CONNECTION_START](state) {
        resetChangesAllErrors(state);
        const osCreatedConnectionState = state.osCreatedConnection;
        osCreatedConnectionState.isProcessing = true;
    },

    [types.OS_CREATED_CONNECTION_SUCCESS](state) {
        const osCreatedConnectionState = state.osCreatedConnection;
        osCreatedConnectionState.isProcessing = false;
    },

    [types.OS_CREATED_CONNECTION_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const osCreatedConnectionState = state.osCreatedConnection;
        osCreatedConnectionState.errorTitle = payload.errorTitle;
        osCreatedConnectionState.errorDetails = payload.errorDetails;
        osCreatedConnectionState.error = true;
        osCreatedConnectionState.isProcessing = false;
    },

    // ノード管理ドメインアカウント接続テスト系
    [types.DOMAIN_CONNECTION_START](state) {
        resetChangesAllErrors(state);
        const domainConnectionState = state.domainConnection;
        domainConnectionState.isProcessing = true;
    },

    [types.DOMAIN_CONNECTION_SUCCESS](state) {
        const domainConnectionState = state.domainConnection;
        domainConnectionState.isProcessing = false;
    },

    [types.DOMAIN_CONNECTION_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const domainConnectionState = state.domainConnection;
        domainConnectionState.errorTitle = payload.errorTitle;
        domainConnectionState.errorDetails = payload.errorDetails;
        domainConnectionState.error = true;
        domainConnectionState.isProcessing = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetChangesAllErrors(state);
        const csvState = state.csv;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { code: string; message: string }) {
        const csvState = state.csv;
        csvState.errorCode = payload.code;
        csvState.errorMsg = payload.message;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    [types.CLEAR_APPS](state) {
        resetChangesAllErrors(state);
    },

    [types.CLEAR_EXCLUDES](state) {
        resetChangesAllErrors(state);
    },

    // 自動ログインアプリ追加系
    [types.ADD_APPS_START](state) {
        resetChangesAllErrors(state);
        const addAppsState = state.addApps;
        addAppsState.isProcessing = true;
    },

    [types.ADD_APPS_SUCCESS](state) {
        const addAppsState = state.addApps;
        addAppsState.isProcessing = false;
    },

    [types.ADD_APPS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const addAppsState = state.addApps;
        addAppsState.errorTitle = payload.errorTitle;
        addAppsState.errorDetails = payload.errorDetails;
        addAppsState.error = true;
        addAppsState.isProcessing = false;
    },

    // 自動ログインアプリ削減系
    [types.REMOVE_APPS_START](state) {
        resetChangesAllErrors(state);
        const removeAppsState = state.removeApps;
        removeAppsState.isProcessing = true;
    },

    [types.REMOVE_APPS_SUCCESS](state) {
        const removeAppsState = state.removeApps;
        removeAppsState.isProcessing = false;
    },

    [types.REMOVE_APPS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const removeAppsState = state.removeApps;
        removeAppsState.errorTitle = payload.errorTitle;
        removeAppsState.errorDetails = payload.errorDetails;
        removeAppsState.error = true;
        removeAppsState.isProcessing = false;
    },

    // ログイン履歴収集除外条件追加系
    [types.ADD_EXCLUDES_START](state) {
        resetChangesAllErrors(state);
        const addExcludesState = state.addExcludes;
        addExcludesState.isProcessing = true;
    },

    [types.ADD_EXCLUDES_SUCCESS](state) {
        const addExcludesState = state.addExcludes;
        addExcludesState.isProcessing = false;
    },

    [types.ADD_EXCLUDES_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const addExcludesState = state.addExcludes;
        addExcludesState.errorTitle = payload.errorTitle;
        addExcludesState.errorDetails = payload.errorDetails;
        addExcludesState.error = true;
        addExcludesState.isProcessing = false;
    },

    // ノード割当ログイン履歴収集除外条件除外系
    [types.REMOVE_EXCLUDES_START](state) {
        resetChangesAllErrors(state);
        const removeExcludesState = state.removeExcludes;
        removeExcludesState.isProcessing = true;
    },

    [types.REMOVE_EXCLUDES_SUCCESS](state) {
        const removeExcludesState = state.removeExcludes;
        removeExcludesState.isProcessing = false;
    },

    [types.REMOVE_EXCLUDES_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const removeExcludesState = state.removeExcludes;
        removeExcludesState.errorTitle = payload.errorTitle;
        removeExcludesState.errorDetails = payload.errorDetails;
        removeExcludesState.error = true;
        removeExcludesState.isProcessing = false;
    },

    // SAMLサービスプロバイダノード検索系
    [types.SAML_NODES_SEARCH_START](state) {
        const samlNodesSearchState = state.samlNodesSearch;
        samlNodesSearchState.error = false;
        samlNodesSearchState.errorCode = undefined;
        samlNodesSearchState.errorMsg = undefined;
        samlNodesSearchState.isProcessing = true;
        samlNodesSearchState.isFetchSuccess = false;
    },

    [types.SAML_NODES_SEARCH_SUCCESS](state, payload: { count: number; nodes: Array<SamlNode> }) {
        state.samlNodes = payload.nodes;
        state.samlNodeCount = payload.count;

        const samlNodesSearchState = state.samlNodesSearch;
        samlNodesSearchState.isProcessing = false;
        samlNodesSearchState.isFetchSuccess = true;
    },

    [types.SAML_NODES_SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const samlNodesSearchState = state.samlNodesSearch;
        samlNodesSearchState.errorCode = payload.code;
        samlNodesSearchState.errorMsg = payload.message;
        samlNodesSearchState.error = true;
        samlNodesSearchState.isProcessing = false;
        samlNodesSearchState.isFetchSuccess = false;
    },

    // SAML連携用の証明書ダウンロード
    [types.SAML_CERTIFICATE_DOWNLOAD_START](state) {
        resetChangesAllErrors(state);
        const samlCertificateDownloadState = state.samlCertificateDownload;
        samlCertificateDownloadState.error = false;
        samlCertificateDownloadState.errorTitle = undefined;
        samlCertificateDownloadState.errorDetails = undefined;
        samlCertificateDownloadState.isProcessing = true;
        samlCertificateDownloadState.isFetchSuccess = false;
    },

    [types.SAML_CERTIFICATE_DOWNLOAD_SUCCESS](state) {
        const samlCertificateDownloadState = state.samlCertificateDownload;
        samlCertificateDownloadState.isProcessing = false;
        samlCertificateDownloadState.isFetchSuccess = true;
    },

    [types.SAML_CERTIFICATE_DOWNLOAD_FAILURE](
        state,
        payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }
    ) {
        const samlCertificateDownloadState = state.samlCertificateDownload;
        samlCertificateDownloadState.errorTitle = payload.errorTitle;
        samlCertificateDownloadState.errorDetails = payload.errorDetails;
        samlCertificateDownloadState.error = true;
        samlCertificateDownloadState.isProcessing = false;
        samlCertificateDownloadState.isFetchSuccess = false;
    },

    // SAML連携用の証明書再発行
    [types.SAML_CERTIFICATE_REISSUE_START](state) {
        resetChangesAllErrors(state);
        const samlCertificateReissueState = state.samlCertificateReissue;
        samlCertificateReissueState.error = false;
        samlCertificateReissueState.errorTitle = undefined;
        samlCertificateReissueState.errorDetails = undefined;
        samlCertificateReissueState.isProcessing = true;
        samlCertificateReissueState.isFetchSuccess = false;
    },

    [types.SAML_CERTIFICATE_REISSUE_SUCCESS](state) {
        const samlCertificateReissueState = state.samlCertificateReissue;
        samlCertificateReissueState.isProcessing = false;
        samlCertificateReissueState.isFetchSuccess = true;
    },

    [types.SAML_CERTIFICATE_REISSUE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const samlCertificateReissueState = state.samlCertificateReissue;
        samlCertificateReissueState.errorTitle = payload.errorTitle;
        samlCertificateReissueState.errorDetails = payload.errorDetails;
        samlCertificateReissueState.error = true;
        samlCertificateReissueState.isProcessing = false;
        samlCertificateReissueState.isFetchSuccess = false;
    },

    // SAMLメタデータダウンロード
    [types.SAML_METADATA_DOWNLOAD_START](state) {
        resetChangesAllErrors(state);
        const samlMetadataDownloadState = state.samlMetadataDownload;
        samlMetadataDownloadState.error = false;
        samlMetadataDownloadState.errorTitle = undefined;
        samlMetadataDownloadState.errorDetails = undefined;
        samlMetadataDownloadState.isProcessing = true;
        samlMetadataDownloadState.isFetchSuccess = false;
    },

    [types.SAML_METADATA_DOWNLOAD_SUCCESS](state) {
        const samlMetadataDownloadState = state.samlMetadataDownload;
        samlMetadataDownloadState.isProcessing = false;
        samlMetadataDownloadState.isFetchSuccess = true;
    },

    [types.SAML_METADATA_DOWNLOAD_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const samlMetadataDownloadState = state.samlMetadataDownload;
        samlMetadataDownloadState.errorTitle = payload.errorTitle;
        samlMetadataDownloadState.errorDetails = payload.errorDetails;
        samlMetadataDownloadState.error = true;
        samlMetadataDownloadState.isProcessing = false;
        samlMetadataDownloadState.isFetchSuccess = false;
    },

    // 証明書再発行後、発行済み証明書の有効期限のみ更新
    [types.SHOW_NEW_SAML_CERT_EXP_AT_START](state) {
        resetChangesAllErrors(state);
        const showState = state.show;
        showState.error = false;
        showState.errorCode = undefined;
        showState.errorMsg = undefined;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_NEW_SAML_CERT_EXP_AT_SUCCESS](
        state,
        payload: {
            node: Node;
            group: Array<NodeGroup>;
            node_password_strength_setting: NodePasswordStrengthSetting;
            node_saml_federation_info: NodeSamlFederationInfo;
            node_placeholders: Array<NodePlaceholder>;
        }
    ) {
        const showState = state.show;
        state.nodeDetails.nodeSamlFederationInfo = payload.node_saml_federation_info;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_NEW_SAML_CERT_EXP_AT_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // ログイン履歴収集間隔タイプ取得
    [types.LOGIN_HISTORY_COLLECTION_INTERVAL_TYPE_ACQUISITION_START](state) {
        resetChangesAllErrors(state);
        const loginHistoryCollectionIntervalTypeAcquisition = state.loginHistoryCollectionIntervalTypeAcquisition;
        loginHistoryCollectionIntervalTypeAcquisition.error = false;
        loginHistoryCollectionIntervalTypeAcquisition.errorTitle = undefined;
        loginHistoryCollectionIntervalTypeAcquisition.errorDetails = undefined;
        loginHistoryCollectionIntervalTypeAcquisition.isProcessing = true;
    },

    [types.LOGIN_HISTORY_COLLECTION_INTERVAL_TYPE_ACQUISITION_SUCCESS](
        state,
        payload: Array<{ text: string; value: string }>
    ) {
        state.loginHistoryCollectionIntervalType = payload;

        const loginHistoryCollectionIntervalTypeAcquisition = state.loginHistoryCollectionIntervalTypeAcquisition;
        loginHistoryCollectionIntervalTypeAcquisition.isProcessing = false;
    },

    [types.LOGIN_HISTORY_COLLECTION_INTERVAL_TYPE_ACQUISITION_FAILURE](
        state,
        payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }
    ) {
        const loginHistoryCollectionIntervalTypeAcquisition = state.loginHistoryCollectionIntervalTypeAcquisition;
        loginHistoryCollectionIntervalTypeAcquisition.errorTitle = payload.errorTitle;
        loginHistoryCollectionIntervalTypeAcquisition.errorDetails = payload.errorDetails;
        loginHistoryCollectionIntervalTypeAcquisition.error = true;
        loginHistoryCollectionIntervalTypeAcquisition.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const updateState = state.update;
        updateState.isProcessing = false;

        const destroyState = state.destroy;
        destroyState.isProcessing = false;

        const osConnectionState = state.osConnection;
        osConnectionState.isProcessing = false;

        const osCreatedConnectionState = state.osCreatedConnection;
        osCreatedConnectionState.isProcessing = false;

        const domainConnectionState = state.domainConnection;
        domainConnectionState.isProcessing = false;

        const csvState = state.csv;
        csvState.isProcessing = false;

        const addAppsState = state.addApps;
        addAppsState.isProcessing = false;

        const removeAppsState = state.removeApps;
        removeAppsState.isProcessing = false;

        const addExcludesState = state.addExcludes;
        addExcludesState.isProcessing = false;

        const removeExcludesState = state.removeExcludes;
        removeExcludesState.isProcessing = false;

        const samlNodesSearchState = state.samlNodesSearch;
        samlNodesSearchState.isProcessing = false;
        samlNodesSearchState.isFetchSuccess = false;

        const samlCertificateDownloadState = state.samlCertificateDownload;
        samlCertificateDownloadState.isProcessing = false;
        samlCertificateDownloadState.isFetchSuccess = false;

        const samlCertificateReissueState = state.samlCertificateReissue;
        samlCertificateReissueState.isProcessing = false;
        samlCertificateReissueState.isFetchSuccess = false;

        const samlMetadataDownloadState = state.samlMetadataDownload;
        samlMetadataDownloadState.isProcessing = false;
        samlMetadataDownloadState.isFetchSuccess = false;

        const loginHistoryCollectionIntervalTypeAcquisition = state.loginHistoryCollectionIntervalTypeAcquisition;
        loginHistoryCollectionIntervalTypeAcquisition.isProcessing = false;
    },

    [types.CLEAR_ALL](state) {
        resetChangesAllErrors(state);
        state.nodes = undefined;
        state.nodeDetails.node = undefined;
        state.nodeDetails.nodeDomains = undefined;
        state.nodeDetails.nodeGroups = undefined;
        state.nodeDetails.nodePasswordStrengthSetting = undefined;
        state.nodeCount = 0;
        state.samlNodes = undefined;
        state.samlNodeCount = 0;
    },
};
